import { useMemo, useState } from "react";

import type { PublicForumPostItem } from "@/features/public/api";

interface ForumCategory {
  id: string;
  name: string;
  color: string;
}

interface ForumPostCardProps {
  categories: ForumCategory[];
  isLoggedIn: boolean;
  post: PublicForumPostItem;
  commenting: boolean;
  liking: boolean;
  onRequireLogin: () => void;
  onToggleLike: (postId: string) => void;
  onComment: (postId: string, payload: { content?: string }) => Promise<void> | void;
}

function formatDateTime(value: string) {
  return new Date(value).toLocaleString("fr-FR", {
    dateStyle: "medium",
    timeStyle: "short"
  });
}

function AuthorAvatar({ name, avatar }: { name: string; avatar: string | null }) {
  if (avatar) {
    return <img src={avatar} alt={name} className="w-12 h-12 rounded-full object-cover object-[center_24%]" />;
  }

  return (
    <div className="w-12 h-12 rounded-full bg-[#E8521A] text-white flex items-center justify-center font-semibold">
      {name.charAt(0).toUpperCase()}
    </div>
  );
}

function CommentsModal({
  comments,
  commenting,
  isLoggedIn,
  onClose,
  onComment,
  onRequireLogin,
  postTitle
}: {
  comments: PublicForumPostItem["comments"];
  commenting: boolean;
  isLoggedIn: boolean;
  onClose: () => void;
  onComment: (content: string) => Promise<void>;
  onRequireLogin: () => void;
  postTitle: string;
}) {
  const [commentText, setCommentText] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);
  const orderedComments = useMemo(
    () =>
      [...comments].sort((left, right) => {
        const leftTime = new Date(left.timestamp).getTime();
        const rightTime = new Date(right.timestamp).getTime();

        if (Number.isNaN(leftTime) || Number.isNaN(rightTime)) {
          return 0;
        }

        return rightTime - leftTime;
      }),
    [comments]
  );

  const handleSubmit = async () => {
    const content = commentText.trim();

    if (!content) {
      setLocalError("Écris un commentaire avant de l'envoyer.");
      return;
    }

    if (!isLoggedIn) {
      onRequireLogin();
      return;
    }

    setLocalError(null);
    await onComment(content);
    setCommentText("");
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="w-full max-w-2xl rounded-[28px] bg-white border border-gray-100 shadow-[0_30px_100px_rgba(0,0,0,0.16)] overflow-hidden"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between gap-4">
          <div>
            <h3 className="text-lg font-bold text-[#1A1A1A]">Commentaires</h3>
            <p className="mt-1 text-sm text-gray-500 line-clamp-1">{postTitle}</p>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-xl hover:bg-gray-100 transition-colors flex items-center justify-center text-gray-500"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        <div className="max-h-[60vh] overflow-y-auto px-6 py-5 space-y-4 bg-[#FCFCFC]">
          {orderedComments.length > 0 ? (
            orderedComments.map((comment) => (
              <div key={comment.id} className="flex gap-3">
                <AuthorAvatar name={comment.author.name} avatar={comment.author.avatar} />
                <div className="flex-1 rounded-2xl bg-white border border-gray-100 p-4">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <span className="font-medium text-sm text-gray-900">{comment.author.name}</span>
                    <span className="text-xs text-gray-400">{formatDateTime(comment.timestamp)}</span>
                  </div>
                  <p className="text-sm text-gray-600 whitespace-pre-line">{comment.content}</p>
                </div>
              </div>
            ))
          ) : (
            <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-4 py-10 text-center text-sm text-gray-400">
              Aucun commentaire pour le moment.
            </div>
          )}
        </div>

        <div className="px-6 py-5 border-t border-gray-100 bg-white space-y-3">
          {isLoggedIn ? (
            <>
              <textarea
                value={commentText}
                onChange={(event) => setCommentText(event.target.value)}
                placeholder="Écrire votre commentaire..."
                rows={3}
                maxLength={1000}
                className="w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors resize-none"
              />
              <div className="flex items-center justify-between gap-3">
                <span className="text-xs text-gray-400">{commentText.length}/1000</span>
                <button
                  type="button"
                  onClick={() => void handleSubmit()}
                  disabled={commenting || !commentText.trim()}
                  className="inline-flex items-center gap-2 rounded-xl bg-[#E8521A] px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-[#d44a16] disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <i className="ri-send-plane-fill"></i>
                  {commenting ? "Envoi..." : "Publier le commentaire"}
                </button>
              </div>
            </>
          ) : (
            <div className="rounded-2xl border border-orange-100 bg-orange-50 px-4 py-4 text-sm text-[#B64B20]">
              Connectez-vous pour publier un commentaire sur cette discussion.
            </div>
          )}
          {localError ? <p className="text-xs text-red-500">{localError}</p> : null}
        </div>
      </div>
    </div>
  );
}

export default function ForumPostCard({
  categories,
  isLoggedIn,
  post,
  commenting,
  liking,
  onRequireLogin,
  onToggleLike,
  onComment
}: ForumPostCardProps) {
  const [showCommentsModal, setShowCommentsModal] = useState(false);

  const category = useMemo(
    () => categories.find((item) => item.id === post.category) ?? categories[0],
    [categories, post.category]
  );

  return (
    <>
      <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
        <div className="flex items-start gap-4 mb-4">
          <AuthorAvatar name={post.author.name} avatar={post.author.avatar} />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h4 className="font-semibold text-gray-900">{post.author.name}</h4>
              <span className={`text-sm px-2 py-0.5 rounded-full text-white ${category?.color || "bg-gray-500"}`}>
                {category?.name || post.category}
              </span>
              <span className="text-sm text-gray-500">{post.author.role}</span>
            </div>
            <span className="text-sm text-gray-400">{formatDateTime(post.timestamp)}</span>
          </div>
        </div>

        <h3 className="text-lg font-bold text-gray-900 mb-2">{post.title}</h3>
        {post.content ? <p className="text-gray-600 mb-4 whitespace-pre-line">{post.content}</p> : null}

        {post.image ? (
          <img
            src={post.image}
            alt={post.title}
            className="w-full h-64 object-cover rounded-lg mb-4"
          />
        ) : null}

        <div className="flex items-center gap-6 pt-4 border-t border-gray-100 flex-wrap">
          <button
            onClick={() => {
              if (isLoggedIn) {
                onToggleLike(post.id);
              }
            }}
            disabled={liking || !isLoggedIn}
            className={`flex items-center gap-2 text-sm transition-colors ${
              post.isLiked ? "text-orange-600" : "text-gray-500 hover:text-orange-600"
            } ${!isLoggedIn ? "cursor-not-allowed opacity-50 hover:text-gray-500" : ""}`}
          >
            <i className={`${post.isLiked ? "ri-heart-fill" : "ri-heart-line"} text-lg`}></i>
            <span>{post.likes}</span>
          </button>
          <button
            onClick={() => setShowCommentsModal(true)}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-teal-600 transition-colors"
          >
            <i className="ri-chat-3-line text-lg"></i>
            <span>{post.comments.length} commentaires</span>
          </button>
          {!isLoggedIn ? (
            <button
              onClick={onRequireLogin}
              className="ml-auto inline-flex items-center gap-2 rounded-full border border-orange-100 bg-orange-50 px-3 py-2 text-sm font-medium text-[#E8521A] transition-colors hover:bg-orange-100"
            >
              <i className="ri-login-box-line"></i>
              Se connecter pour participer
            </button>
          ) : null}
        </div>
      </div>

      {showCommentsModal ? (
        <CommentsModal
          comments={post.comments}
          commenting={commenting}
          isLoggedIn={isLoggedIn}
          onClose={() => setShowCommentsModal(false)}
          onComment={(content) => Promise.resolve(onComment(post.id, { content }))}
          onRequireLogin={onRequireLogin}
          postTitle={post.title}
        />
      ) : null}
    </>
  );
}
